
import { Component, Vue, Prop, Model, Watch } from 'vue-property-decorator';

@Component
export default class FormInput extends Vue {
  @Model('input', { default: '' }) readonly value!: string | number;
  @Prop({ default: '' }) readonly label!: string;
  @Prop({ default: 'text' }) readonly type!: string;
  @Prop({ default: '' }) readonly placeholder!: string;
  @Prop({ default: false }) readonly required!: boolean;
  @Prop({ default: false }) readonly disabled!: boolean;
  @Prop({ default: '' }) readonly help!: string;
  @Prop({ default: '' }) readonly errorMessage!: string;
  @Prop({ default: false }) readonly loading!: boolean;
  @Prop({ default: false }) readonly pasteDisabled!: boolean;
  @Prop({ default: false }) readonly dropDisabled!: boolean;
  @Prop({ default: 'off' }) readonly autocomplete!: string;

  private isFocused = false;
  private hasBeenBlurred = false;

  get inputId(): string {
    return `input-${this.label.replace(/\s+/g, '-').toLowerCase()}`
  }

  get hasError(): boolean {
    return Boolean(this.errorMessage) || (this.required && !this.value && this.hasBeenBlurred);
  }

  get inputClasses(): string {
    const baseClasses = [
      'w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 text-lg',
      'focus:outline-none focus:ring-4 placeholder-gray-400',
      'disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500'
    ];

    if (this.hasError) {
      baseClasses.push(
        'border-red-300 focus:border-red-500 focus:ring-red-500/20',
        'bg-red-50'
      );
    } else if (this.isFocused) {
      baseClasses.push(
        'border-verde-institucional focus:border-verde-institucional focus:ring-verde-institucional/20',
        'bg-white'
      );
    } else {
      baseClasses.push(
        'border-gray-300 hover:border-gray-400 focus:border-verde-institucional focus:ring-verde-institucional/20',
        'bg-white hover:bg-gray-50'
      );
    }

    if (this.$slots.icon || this.loading) {
      baseClasses.push('pr-10');
    }

    return baseClasses.join(' ');
  }

  handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.$emit('input', this.type === 'number' ? Number(target.value) : target.value);
  }

  handleFocus(): void {
    this.isFocused = true;
    this.$emit('focus');
  }

  handleBlur(): void {
    this.isFocused = false;
    this.hasBeenBlurred = true;
    this.$emit('blur');
  }

  handlePaste(event: ClipboardEvent): void {
    if (this.pasteDisabled) {
      event.preventDefault();
    }
  }

  handleDrop(event: DragEvent): void {
    if (this.dropDisabled) {
      event.preventDefault();
    }
  }

  // Focus method for parent component access
  focus(): void {
    const input = this.$refs[this.inputId] as HTMLInputElement;
    if (input) {
      input.focus();
    }
  }
}
